// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getDataViewKey } from "@insite/client-framework/Store/Data/DataState";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CategoryCard from "@rpsc/App/Components/Category/CategoryCard";
import XngCarousel, { SwiperStyle } from "@rpsc/App/Components/XngCarousel/XngCarousel";
import loadFeaturedCategories from "@rpsc/App/Handlers/XNG/FeaturedCategories";
import { CustomAppState } from "@rpsc/App/Store/Reducers";
import { XNGAGE_CUSTOM_WIDGET } from "@rpsc/XDL/constants";
import { isTouchDevice } from "@rpsc/XDL/Utilities";
import * as React from "react";
import { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

const enum fields {
    selectedCategories = "selectedCategories",
    maxNumberOfCategories = "maxNumberOfCategories",
    scrollable = "scrollable",
    responsive = "responsive",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.selectedCategories]: string[];
        [fields.maxNumberOfCategories]: number;
        [fields.scrollable]: boolean;
        [fields.responsive]: boolean;
    };
}

const mapStateToProps = (state: CustomAppState, props: OwnProps) => {
    const key = getDataViewKey({
        id: props.fields.selectedCategories,
    });
    return {
        featuredCategories: state.xFeaturedCategoriesData.data?.[key] || { list: [] },
        isTabletScreen: state.xCommon.isTabletScreen,
    };
};

const mapDispatchToProps = {
    loadFeaturedCategories,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface FeaturedCategoriesStyles {
    loadingWrapper?: InjectableCss;
    swiperStyles?: SwiperStyle;
}

export const featuredCategoriesStyles: FeaturedCategoriesStyles = {};
const styles = featuredCategoriesStyles;

const FeaturedCategories: React.FC<Props> = ({
    fields: { selectedCategories, maxNumberOfCategories, scrollable, responsive },
    loadFeaturedCategories,
    featuredCategories,
    isTabletScreen,
}) => {
    const [key, setKey] = useState(0);
    useEffect(() => {
        setKey(prevKey => prevKey + 1);
    }, [isTabletScreen]);

    useEffect(() => {
        if (!!selectedCategories.length && !featuredCategories?.isLoading) {
            loadFeaturedCategories({
                id: selectedCategories,
            });
        }
    }, [selectedCategories]);

    if (featuredCategories.isLoading) {
        return (
            <StyledWrapper {...styles.loadingWrapper} data-test-selector="featuredCategories_Loading">
                <LoadingSpinner />
            </StyledWrapper>
        );
    }

    if (!featuredCategories.list?.length) {
        return null;
    }
    const isScrollable = scrollable || isTabletScreen;
    return (
        <XngCarousel
            key={key}
            extendedStyles={styles.swiperStyles}
            scrollbar={isScrollable}
            spaceBetween={40}
            rewind={!isScrollable}
            navigation={!isTabletScreen}
            draggable={isTouchDevice()}
            slideWidth="204px" // In case its responsive
            breakpoints={
                responsive
                    ? undefined
                    : {
                          375: {
                              slidesPerView: 1.5,
                          },
                          500: {
                              slidesPerView: 2,
                          },
                          768: {
                              slidesPerView: 3.5,
                          },
                          1024: {
                              slidesPerView: 4,
                          },
                          1365: {
                              slidesPerView: 5.5,
                          },
                          1601: {
                              slidesPerView: 6.5,
                          },
                      }
            }
        >
            {featuredCategories.list?.slice(0, maxNumberOfCategories).map((category, index) => (
                <CategoryCard key={`${category.id}_${index}`} category={category} />
            ))}
        </XngCarousel>
    );
};

const featuredCategories: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(FeaturedCategories),
    definition: {
        group: XNGAGE_CUSTOM_WIDGET as any,
        fieldDefinitions: [
            {
                name: fields.selectedCategories,
                displayName: "Select Category",
                editorTemplate: "CategoriesField",
                defaultValue: [],
                fieldType: "General",
            },
            {
                name: fields.maxNumberOfCategories,
                displayName: "Max Number Of Category",
                editorTemplate: "IntegerField",
                defaultValue: 7,
                max: 25,
                min: 1,
                fieldType: "General",
            },
            {
                name: fields.scrollable,
                displayName: "Add Scrollbar",
                editorTemplate: "CheckboxField",
                fieldType: "General",
                defaultValue: false,
            },
            {
                name: fields.responsive,
                displayName: "Responsive Mode",
                editorTemplate: "CheckboxField",
                fieldType: "General",
                defaultValue: false,
            },
        ],
    },
};

export default featuredCategories;
